/**
 * Bridges mapped as areas (`man_made=bridge`): one deck for all the ways on
 * it. Brunnsbron over Stora Hamnkanalen is a 40 by 15 m outline with a dozen
 * short ways on it, a street, its tram tracks, footways, a path and a
 * cycleway; each was a bridge of its own, its own height, slab, beams and
 * railings, stacked over each other in a mess. Within such an outline the
 * ways are laid at one height, its deck's (`deckAt`, used by `roadprofile.ts`
 * and `bridges.ts`), and build nothing of their own: the outline is the
 * bridge, built here.
 *
 * Only an outline over water is built so; one over a road is left to its
 * ways (see below). Its deck is level, at the middle of the street just outside its outline
 * where it meets land, as a city's canal bridges are. Built: the top, a few
 * centimetres under the ways on it; the slab's underside and its sides;
 * and a parapet along each side over the water.
 *
 * Pure: no fetch, no DOM.
 */

import { SurfaceIndex } from './surfaceindex.js';

/** From the deck's top to its underside, metres; and its top under the ways on it. */
const DEPTH = 0.9;
const UNDER_WAYS = 0.04;
/** The parapet: how high over the deck, and how thick. */
const PARAPET = 1.0;
/** How far under the deck the ground must be for it to be bridged there, metres. */
const CLEAR = 0.5;
const THICK = 0.3;
/** The pieces its triangles are cut into to find where it bridges, metres across at most. */
const PIECE = 2;
/** A triangle this big or bigger is cut even if all its corners agree, metres: water may lie between them. */
const WHOLE = 8;
/** How far outside its outline the street an area meets is read, metres. */
const AROUND = [3, 6];
/** Longest a flat canal bridge outline is, metres: a river viaduct rises over roads and rails on its own profile. */
const MAX_SPAN = 80;

/** Linear RGB: the deck's paving, its slab, and the parapet's stone. */
const PAVING = [0.31, 0.3, 0.28];
const SLAB = [0.24, 0.23, 0.22];
const STONE = [0.36, 0.35, 0.33];

export interface BridgeAreas {
  /** The deck's height at (x, z), if (x, z) is on one. */
  deckAt: (x: number, z: number) => number | undefined;
  mesh: { positions: Float32Array; colors: Float32Array; indices: Uint32Array };
  /** The decks' tops, as ground to stand on. */
  floor: { positions: Float32Array; indices: Uint32Array };
  /** The parapets, x0, z0, x1, z1, bottom, top each, to collide with. */
  walls: Float32Array;
}

export function buildBridgeAreas(
  positions: Float32Array | undefined, indices: Uint32Array | undefined, ranges: Uint32Array | undefined,
  land: (x: number, z: number) => number, water: (x: number, z: number) => boolean,
): BridgeAreas {
  const none: BridgeAreas = {
    deckAt: () => undefined,
    mesh: { positions: new Float32Array(0), colors: new Float32Array(0), indices: new Uint32Array(0) },
    floor: { positions: new Float32Array(0), indices: new Uint32Array(0) }, walls: new Float32Array(0),
  };
  if (positions === undefined || indices === undefined || ranges === undefined || indices.length === 0) return none;
  const count = positions.length / 2;
  // Each area's height: the street it meets, the middle of the ground just
  // outside its outline on land. Read on the outline itself it was the
  // elevation model's, which smears the canal under it into its edges: a
  // canal's polygon is cut round the bridge, and Brunnsbron's deck lay
  // 0.34 m up, two metres under the streets at its ends.
  const flat = new Float32Array(count * 3);
  for (let v = 0; v < count; v++) { flat[v * 3] = positions[v * 2]; flat[v * 3 + 2] = positions[v * 2 + 1]; }
  const outline = new SurfaceIndex([{ positions: flat, indices }]);
  const level = new Float32Array(count);
  for (let r = 0; r + 1 < ranges.length; r += 2) {
    const start = ranges[r], n = ranges[r + 1];
    const outside: number[] = [], all: number[] = [];
    for (let v = start; v < start + n; v++) {
      const x = positions[v * 2], z = positions[v * 2 + 1];
      all.push(land(x, z));
      let best = -Infinity;
      for (let k = 0; k < 8; k++) {
        const a = (k / 8) * Math.PI * 2;
        for (const d of AROUND) {
          const sx = x + Math.cos(a) * d, sz = z + Math.sin(a) * d;
          if (outline.heightAt(sx, sz, Infinity) !== undefined || water(sx, sz)) continue;
          best = Math.max(best, land(sx, sz));
        }
      }
      if (best > -Infinity) outside.push(best);
    }
    const pick = (outside.length > 0 ? outside : all).sort((a, b) => a - b);
    const h = pick.length > 0 ? pick[pick.length >> 1] : 0;
    for (let v = start; v < start + n; v++) level[v] = h;
  }
  // Only an outline over water is a deck of its own. A footbridge high over
  // a road — Landvetter's over Härrydavägen is mapped as an outline too —
  // has the road under it as the ground round it, and laid at that it was no
  // bridge at all: its ways, sloping up their ramps, build it as before.
  const areaOf = new Int32Array(count);
  const small = new Set<number>();
  for (let r = 0; r + 1 < ranges.length; r += 2) {
    const start = ranges[r], n = ranges[r + 1];
    areaOf.fill(r, start, start + n);
    let minX = Infinity, minZ = Infinity, maxX = -Infinity, maxZ = -Infinity;
    for (let v = start; v < start + n; v++) {
      const x = positions[v * 2], z = positions[v * 2 + 1];
      minX = Math.min(minX, x); maxX = Math.max(maxX, x);
      minZ = Math.min(minZ, z); maxZ = Math.max(maxZ, z);
    }
    if (Math.hypot(maxX - minX, maxZ - minZ) <= MAX_SPAN) small.add(r);
  }
  const wet = new Set<number>();
  for (let t = 0; t + 2 < indices.length; t += 3) {
    const area = areaOf[indices[t]];
    if (wet.has(area) || !small.has(area)) continue;
    const [ax, az, bx, bz, cx, cz] = [indices[t], indices[t + 1], indices[t + 2]].flatMap(v => [positions[v * 2], positions[v * 2 + 1]]);
    const n = Math.min(40, Math.ceil(Math.max(Math.hypot(bx - ax, bz - az), Math.hypot(cx - bx, cz - bz), Math.hypot(ax - cx, az - cz)) / PIECE));
    for (let i = 0; i <= n && !wet.has(area); i++) {
      for (let j = 0; i + j <= n; j++) {
        const u = i / n, w = j / n;
        if (water(ax + (bx - ax) * u + (cx - ax) * w, az + (bz - az) * u + (cz - az) * w)) { wet.add(area); break; }
      }
    }
  }
  const kept: number[] = [];
  for (let t = 0; t + 2 < indices.length; t += 3) if (wet.has(areaOf[indices[t]])) kept.push(indices[t], indices[t + 1], indices[t + 2]);
  if (kept.length === 0) return none;
  indices = new Uint32Array(kept);
  const xyz = new Float32Array(count * 3);
  for (let v = 0; v < count; v++) { xyz[v * 3] = positions[v * 2]; xyz[v * 3 + 1] = level[v]; xyz[v * 3 + 2] = positions[v * 2 + 1]; }
  const index = new SurfaceIndex([{ positions: xyz, indices }]);

  const out = { positions: [] as number[], colors: [] as number[], indices: [] as number[] };
  const floor = { positions: [] as number[], indices: [] as number[] };
  const walls: number[] = [];
  const tri = (a: number[], b: number[], c: number[], col: number[]) => {
    const base = out.positions.length / 3;
    out.positions.push(...a, ...b, ...c);
    for (let k = 0; k < 3; k++) out.colors.push(col[0], col[1], col[2]);
    out.indices.push(base, base + 1, base + 2);
  };
  const quad = (a: number[], b: number[], c: number[], d: number[], col: number[]) => {
    tri(a, b, c, col);
    tri(a, c, d, col);
  };
  // Only where it bridges: over the water, or ground well under it. Over the
  // land at its ends the streets lie on the ground, and a deck there stood
  // over the square and over the road beside it. Its triangles are big, a
  // few across a bridge, so each is cut into pieces a couple of metres across
  // and a piece kept if any of it bridges.
  const bridges = (x: number, z: number, h: number) => water(x, z) || land(x, z) < h - CLEAR;
  const pieces: number[] = [], pieceLevel: number[] = [];
  const split = (ax: number, az: number, bx: number, bz: number, cx: number, cz: number, h: number, depth: number) => {
    const ab = Math.hypot(bx - ax, bz - az), bc = Math.hypot(cx - bx, cz - bz), ca = Math.hypot(ax - cx, az - cz);
    const longest = Math.max(ab, bc, ca);
    const gx = (ax + bx + cx) / 3, gz = (az + bz + cz) / 3;
    const tests = [bridges(gx, gz, h), bridges(ax, az, h), bridges(bx, bz, h), bridges(cx, cz, h)];
    const agree = tests.every(t => t === tests[0]);
    // Cut only where it is part over the water, or too big to tell.
    if (longest > PIECE && depth < 12 && (!agree || longest > WHOLE)) {
      // Halve its longest edge.
      if (longest === ab) { const mx = (ax + bx) / 2, mz = (az + bz) / 2; split(ax, az, mx, mz, cx, cz, h, depth + 1); split(mx, mz, bx, bz, cx, cz, h, depth + 1); }
      else if (longest === bc) { const mx = (bx + cx) / 2, mz = (bz + cz) / 2; split(ax, az, bx, bz, mx, mz, h, depth + 1); split(ax, az, mx, mz, cx, cz, h, depth + 1); }
      else { const mx = (cx + ax) / 2, mz = (cz + az) / 2; split(ax, az, bx, bz, mx, mz, h, depth + 1); split(mx, mz, bx, bz, cx, cz, h, depth + 1); }
      return;
    }
    if (!tests.some(t => t)) return;
    pieces.push(ax, az, bx, bz, cx, cz);
    pieceLevel.push(h);
  };
  for (let t = 0; t + 2 < indices.length; t += 3) {
    const [a, b, c] = [indices[t], indices[t + 1], indices[t + 2]];
    split(positions[a * 2], positions[a * 2 + 1], positions[b * 2], positions[b * 2 + 1], positions[c * 2], positions[c * 2 + 1], level[a], 0);
  }
  for (let p = 0; p < pieceLevel.length; p++) {
    const h = pieceLevel[p], q = pieces.slice(p * 6, p * 6 + 6);
    const pt = (k: number, dy: number) => [q[k * 2], h + dy, q[k * 2 + 1]];
    // Its top, facing up, and its underside, facing down.
    const top = [pt(0, -UNDER_WAYS), pt(1, -UNDER_WAYS), pt(2, -UNDER_WAYS)];
    tri(top[0], top[2], top[1], PAVING);
    tri(top[0], top[1], top[2], PAVING);
    const fb = floor.positions.length / 3;
    floor.positions.push(...top[0], ...top[1], ...top[2]);
    floor.indices.push(fb, fb + 1, fb + 2);
    tri(pt(0, -DEPTH), pt(1, -DEPTH), pt(2, -DEPTH), SLAB);
  }
  // Its outline: the pieces' edges only one of them has. Its slab's side down
  // each, and a parapet along each with water outside it.
  const key = (x: number, z: number) => `${Math.round(x * 100)},${Math.round(z * 100)}`;
  const edges = new Map<string, { p: number; e: number; n: number }>();
  for (let p = 0; p < pieceLevel.length; p++) {
    for (let e = 0; e < 3; e++) {
      const a = key(pieces[p * 6 + e * 2], pieces[p * 6 + e * 2 + 1]), b = key(pieces[p * 6 + ((e + 1) % 3) * 2], pieces[p * 6 + ((e + 1) % 3) * 2 + 1]);
      const k = a < b ? `${a}|${b}` : `${b}|${a}`;
      const had = edges.get(k);
      if (had === undefined) edges.set(k, { p, e, n: 1 }); else had.n++;
    }
  }
  for (const { p, e, n } of edges.values()) {
    if (n !== 1) continue;
    const q = pieces.slice(p * 6, p * 6 + 6), h = pieceLevel[p];
    const ax = q[e * 2], az = q[e * 2 + 1], bx = q[((e + 1) % 3) * 2], bz = q[((e + 1) % 3) * 2 + 1];
    const cx = q[((e + 2) % 3) * 2], cz = q[((e + 2) % 3) * 2 + 1];
    const ex = bx - ax, ez = bz - az, len = Math.hypot(ex, ez);
    if (len < 0.05) continue;
    // Outward: away from the piece's third corner.
    let nx = -ez / len, nz = ex / len;
    if ((cx - ax) * nx + (cz - az) * nz > 0) { nx = -nx; nz = -nz; }
    const top0 = [ax, h - UNDER_WAYS, az], top1 = [bx, h - UNDER_WAYS, bz];
    const low0 = [ax, h - DEPTH, az], low1 = [bx, h - DEPTH, bz];
    quad(low0, low1, top1, top0, SLAB);
    quad(low1, low0, top0, top1, SLAB);
    // A parapet on its outline with water outside, not along where its pieces meet.
    const mx = (ax + bx) / 2, mz = (az + bz) / 2;
    if (index.heightAt(mx + nx * 0.3, mz + nz * 0.3, Infinity) !== undefined || !water(mx + nx * 2, mz + nz * 2)) continue;
    // The parapet: a low stone wall along the edge, its outer face flush with the slab's side.
    const i0 = [top0[0] - nx * THICK, top0[1], top0[2] - nz * THICK], i1 = [top1[0] - nx * THICK, top1[1], top1[2] - nz * THICK];
    const up = (v: number[]) => [v[0], v[1] + PARAPET, v[2]];
    quad(top0, top1, up(top1), up(top0), STONE);
    quad(top1, top0, up(top0), up(top1), STONE);
    quad(i1, i0, up(i0), up(i1), STONE);
    quad(i0, i1, up(i1), up(i0), STONE);
    quad(up(top0), up(top1), up(i1), up(i0), STONE);
    walls.push(ax - nx * THICK / 2, az - nz * THICK / 2, bx - nx * THICK / 2, bz - nz * THICK / 2, top0[1] - DEPTH, top0[1] + PARAPET);
  }
  return {
    deckAt: (x, z) => index.heightAt(x, z, Infinity),
    mesh: { positions: new Float32Array(out.positions), colors: new Float32Array(out.colors), indices: new Uint32Array(out.indices) },
    floor: { positions: new Float32Array(floor.positions), indices: new Uint32Array(floor.indices) },
    walls: new Float32Array(walls),
  };
}
